import React, { useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { FileText, Image, Plus, X } from 'lucide-react';
import { KeyTypeAttachment } from '../../types/keyRegister';
import { KEY_TYPE_ATTACHMENT_EXTENSIONS } from '../../Helpers/keyRegisterUtils';
import { formatFileSize } from '../../Helpers/FileUtils';
import ConfirmationModal from '../../Components/ConfirmationModal';
import { useStorageUrls } from './useStorageUrls';

interface LockScheduleModalProps {
    show: boolean;
    onHide: () => void;
    /** Newest first. */
    files: KeyTypeAttachment[];
    getUrl: (storagePath: string) => Promise<string>;
    onAdd: (files: File[]) => Promise<void>;
    onRemove: (fileKey: string) => Promise<void>;
}

/**
 * Låsschema: the register's own files (master-key charts from the locksmith), not tied to a type
 * or building. A chosen file is uploaded at once and a removal is confirmed first, so there is no Spara.
 */
const LockScheduleModal: React.FC<LockScheduleModalProps> = ({ show, onHide, files, getUrl, onAdd, onRemove }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState<File[]>([]);
    const [removeFor, setRemoveFor] = useState<KeyTypeAttachment | null>(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const urls = useStorageUrls(show ? files.map(f => f.storagePath) : [], getUrl);
    const busy = uploading.length > 0 || removing;

    const add = async (chosen: File[]) => {
        if (chosen.length === 0) return;
        setError(null);
        setUploading(chosen);
        try {
            await onAdd(chosen);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Kunde inte ladda upp filerna');
        } finally {
            setUploading([]);
        }
    };

    const confirmRemove = async () => {
        if (!removeFor) return;
        setError(null);
        setRemoving(true);
        try {
            await onRemove(removeFor.key);
            setRemoveFor(null);
        } catch (e) {
            setRemoveFor(null);
            setError(e instanceof Error ? e.message : 'Kunde inte ta bort filen');
        } finally {
            setRemoving(false);
        }
    };

    const close = () => {
        if (busy) return;
        setError(null);
        onHide();
    };

    return (
        <>
            <Modal show={show && !removeFor} onHide={close} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Låsschema</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {files.length === 0 && uploading.length === 0 && (
                        <p className="text-muted">Inga filer ännu. Lägg till låsschemat från låssmeden, till exempel som PDF.</p>
                    )}
                    {(files.length > 0 || uploading.length > 0) && (
                        <ul className="lock-schedule-list" aria-label="Filer">
                            {uploading.map((file, index) => (
                                <li key={`uploading-${index}`} className="lock-schedule-row">
                                    <Spinner animation="border" size="sm" aria-hidden="true" />
                                    <span className="lock-schedule-name">{file.name}</span>
                                    <span className="lock-schedule-meta">Laddar upp…</span>
                                </li>
                            ))}
                            {files.map(file => {
                                const Icon = file.mimeType.startsWith('image/') ? Image : FileText;
                                const url = urls[file.storagePath];
                                return (
                                    <li key={file.key} className="lock-schedule-row">
                                        <Icon size={16} className="text-muted flex-shrink-0" aria-hidden="true" />
                                        <span className="lock-schedule-name">
                                            {url
                                                ? <a href={url} target="_blank" rel="noopener noreferrer">{file.fileName}</a>
                                                : file.fileName}
                                        </span>
                                        <span className="lock-schedule-meta">{file.date.slice(0, 10)} · {formatFileSize(file.fileSize)}</span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0 text-danger"
                                            aria-label={`Ta bort ${file.fileName}`}
                                            disabled={busy}
                                            onClick={() => setRemoveFor(file)}
                                        >
                                            <X size={18} aria-hidden="true" />
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        hidden
                        accept={KEY_TYPE_ATTACHMENT_EXTENSIONS.join(',')}
                        data-testid="lock-schedule-file-input"
                        onChange={e => {
                            add(Array.from(e.target.files ?? []));
                            e.target.value = '';
                        }}
                    />
                    <Button variant="outline-primary" disabled={busy} onClick={() => inputRef.current?.click()}>
                        <Plus size={16} className="me-1" aria-hidden="true" />Lägg till fil
                    </Button>
                    <div className="form-text text-muted">PDF, Word, Excel eller bilder.</div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={close} disabled={busy}>Stäng</Button>
                </Modal.Footer>
            </Modal>

            <ConfirmationModal
                show={!!removeFor}
                title="Ta bort fil"
                message={`Ta bort filen "${removeFor?.fileName ?? ''}" från låsschemat?`}
                confirmText="Ta bort filen"
                variant="danger"
                loading={removing}
                onConfirm={confirmRemove}
                onCancel={() => setRemoveFor(null)}
            />
        </>
    );
};

export default LockScheduleModal;
